import { Box, Stack, Typography } from "@mui/material";
import { isEmpty } from "lodash-es";
import React from "react";
import { CombinedError } from "urql";

import { IErrorInterface } from "../../generated/types";
import { GQLValueForTypeKey } from "../../utils/isGQLValueOfType";

export interface ITypedErrorInterface extends IErrorInterface {
    __typename?: string;
}

export type CustomGqlErrorRenderers<TGqlError> = TGqlError extends { __typename: string }
    ? { [Type in TGqlError["__typename"]]?: (error: GQLValueForTypeKey<TGqlError, Type>) => React.ReactNode }
    : never;

export interface ICombinedErrorMessagesProps<TGqlError extends ITypedErrorInterface> {
    /**
     * A standard FE-generated error.
     */
    error?: Error;
    /**
     * Usually a top-level error returned by URQL.
     */
    combinedError?: CombinedError;
    /**
     * Our internal GQL error interface.
     */
    gqlErrors?: TGqlError[];

    /**
     * Custom renderers for GQL error messages.
     */
    customGqlErrorRenderers?: CustomGqlErrorRenderers<TGqlError>;
}
export namespace ICombinedErrorMessagesProps {
    export function isError<TGqlError extends ITypedErrorInterface>({
        error,
        combinedError,
        gqlErrors,
    }: ICombinedErrorMessagesProps<TGqlError>): boolean {
        return error != null || combinedError != null || !isEmpty(gqlErrors);
    }

    export function hasMessages<TGqlError extends ITypedErrorInterface>({
        error,
        combinedError,
        gqlErrors,
    }: ICombinedErrorMessagesProps<TGqlError>): boolean {
        return error != null || combinedError?.networkError != null || !isEmpty(gqlErrors);
    }
}

export function CombinedErrorMessages<TGqlError extends ITypedErrorInterface = IErrorInterface>(
    props: React.PropsWithChildren<ICombinedErrorMessagesProps<TGqlError>>,
): React.ReactElement | null {
    const {
        error,
        combinedError,
        gqlErrors,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        customGqlErrorRenderers = {} as never,
    } = props;

    function renderErrorMessage(message: string) {
        return (
            <Typography variant="inherit" color="inherit">
                {message}
            </Typography>
        );
    }

    function defaultRenderer({ message }: TGqlError) {
        return renderErrorMessage(message);
    }

    function getMessages(): React.ReactNode[] | undefined {
        if (!ICombinedErrorMessagesProps.isError(props)) {
            return undefined;
        }

        if (gqlErrors != null && gqlErrors.length > 0) {
            return gqlErrors.flatMap((gqlError) => {
                const { __typename, stackTrace } = gqlError;
                const customRenderer =
                    __typename == null ? undefined : customGqlErrorRenderers[__typename as TGqlError["__typename"]];
                return [
                    (customRenderer ?? defaultRenderer)(gqlError as never),
                    stackTrace ? (
                        <Box whiteSpace="pre-wrap" pt={2} fontFamily="monospace">
                            {stackTrace}
                        </Box>
                    ) : undefined,
                ];
            });
        } else if (combinedError != null) {
            const { message: _rootMessage, graphQLErrors, networkError } = combinedError;
            if (networkError != null) {
                return [renderErrorMessage(`Network error: ${networkError.message}`)];
            } else if (graphQLErrors.length > 0) {
                // GraphQL top level errors are not pretty-formatted, let's use default behavior.
            }
            return [];
        } else if (error != null) {
            return [renderErrorMessage(error.message)];
        }
        return undefined;
    }

    const messages = getMessages();
    if (messages == null || messages.length === 0) {
        return null;
    }

    return (
        <Stack>
            {messages.map((message, i) => (
                <React.Fragment key={i}>{message}</React.Fragment>
            ))}
        </Stack>
    );
}
